/**
 * Admin — Firma Detayı
 *
 *   /admin/firmalar/:id
 *
 * Üyeler, son sınavlar, son attempt'ler — hepsi tek sayfada.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface MemberRow {
  id: string;
  role: 'OWNER' | 'ADMIN' | 'VIEWER';
  joinedAt: string;
  isActive: boolean;
  user: {
    id: string;
    email: string;
    name: string | null;
    emailVerified: boolean;
    isActive: boolean;
    lastSeenAt: string | null;
  };
}

interface ExamRow {
  id: string;
  accessCode: string;
  createdAt: string;
  questionCount: number;
  durationMin: number;
  anonymousMode: boolean;
  validUntil: string;
  _count: { attempts: number };
}

interface AttemptRow {
  id: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  totalScore: number | null;
  scoreLevel: string | null;
  certificateId: string | null;
  candidate: {
    fullName: string | null;
    email: string | null;
    anonymousId: string | null;
  };
  exam: { accessCode: string; anonymousMode: boolean };
}

interface OrgDetail {
  id: string;
  name: string;
  industry: string;
  teamSize: number | null;
  bmuLeadStatus: string;
  internalNotes: string | null;
  createdAt: string;
  deletedAt: string | null;
  owner: {
    id: string;
    email: string;
    name: string | null;
    isActive: boolean;
  } | null;
  members: MemberRow[];
  exams: ExamRow[];
}

const ROLE_COLORS: Record<string, string> = {
  OWNER: 'bg-purple-50 text-purple-900',
  ADMIN: 'bg-blue-50 text-blue-900',
  VIEWER: 'bg-slate-100 text-slate-700',
};

const STATUS_COLORS: Record<string, string> = {
  IN_PROGRESS: 'bg-amber-50 text-amber-800',
  COMPLETED: 'bg-green-50 text-green-800',
  EXPIRED: 'bg-red-50 text-red-700',
  ABANDONED: 'bg-slate-100 text-slate-600',
};

export function AdminOrgDetailPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending } = useSession();
  const params = useParams<{ id: string }>();
  const orgId = params.id ?? '';
  const [data, setData] = useState<{
    org: OrgDetail;
    recentAttempts: AttemptRow[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddExam, setShowAddExam] = useState(false);

  async function refresh(): Promise<void> {
    try {
      const result = await api.get<{
        org: OrgDetail;
        recentAttempts: AttemptRow[];
      }>(`/api/admin/orgs/${orgId}`);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Detay yüklenemedi');
    }
  }

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, session, isPending]);

  if (isPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <Alert variant="danger">{error}</Alert>
        </main>
      </div>
    );
  }

  const { org, recentAttempts } = data;
  const completedAttempts = recentAttempts.filter(
    (a) => a.status === 'COMPLETED',
  );
  const teamAvg =
    completedAttempts.length > 0
      ? Math.round(
          completedAttempts.reduce((s, a) => s + (a.totalScore ?? 0), 0) /
            completedAttempts.length,
        )
      : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation('/admin/firmalar')}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Firmalar
        </button>

        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{org.name}</h1>
            <p className="text-sm text-slate-600">
              {org.industry}
              {org.teamSize && ` · ${org.teamSize} kişi`}
              {' · '}
              Lead: <strong>{org.bmuLeadStatus}</strong>
              {' · '}
              Oluşturuldu: {new Date(org.createdAt).toLocaleDateString('tr-TR')}
            </p>
          </div>
          {org.deletedAt && (
            <span className="rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
              SİLİNMİŞ ({new Date(org.deletedAt).toLocaleDateString('tr-TR')})
            </span>
          )}
        </div>

        {/* Metrikler */}
        <div className="mt-6 grid gap-4 sm:grid-cols-4">
          <Metric label="Üye" value={String(org.members.length)} />
          <Metric label="Sınav" value={String(org.exams.length)} />
          <Metric label="Tamamlanan Aday" value={String(completedAttempts.length)} />
          <Metric
            label="Ekip Ort."
            value={teamAvg !== null ? `${teamAvg}/100` : '—'}
          />
        </div>

        {/* Üyeler */}
        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Üyeler ({org.members.length})
            </h2>
            <Button size="sm" onClick={() => setShowAddMember(true)}>
              + Yeni Üye
            </Button>
          </div>

          {showAddMember && (
            <AddMemberModal
              orgId={org.id}
              orgName={org.name}
              onClose={() => setShowAddMember(false)}
              onCreated={async () => {
                setShowAddMember(false);
                await refresh();
              }}
            />
          )}

          {org.members.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Henüz üye yok.</p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {org.members.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <Link
                      href={`/admin/kullanicilar?q=${encodeURIComponent(m.user.email)}`}
                      className="font-medium text-slate-900 hover:text-blue-900 hover:underline"
                    >
                      {m.user.name ?? m.user.email}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {m.user.email}
                      {!m.user.emailVerified && ' · doğrulanmamış'}
                      {!m.user.isActive && ' · pasif'}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      ROLE_COLORS[m.role] ?? 'bg-slate-100'
                    }`}
                  >
                    {m.role}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Sınavlar + Pre-provisioned Adaylar */}
        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Sınavlar ({org.exams.length})
            </h2>
            <Button size="sm" onClick={() => setShowAddExam(true)}>
              + Yeni Sınav
            </Button>
          </div>

          {showAddExam && (
            <AddExamModal
              orgId={org.id}
              onClose={() => setShowAddExam(false)}
              onCreated={async () => {
                setShowAddExam(false);
                await refresh();
              }}
            />
          )}

          {org.exams.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">
              Henüz sınav yok. Önce sınav oluşturun, sonra adaylar ekleyin.
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              {org.exams.map((e) => (
                <ExamWithCandidates
                  key={e.id}
                  examId={e.id}
                  accessCode={e.accessCode}
                  questionCount={e.questionCount}
                  durationMin={e.durationMin}
                  anonymousMode={e.anonymousMode}
                  validUntil={e.validUntil}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Son attempt'ler */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Son Aday Denemeleri ({recentAttempts.length})
          </h2>
          {recentAttempts.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Henüz deneme yok.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Aday</th>
                    <th className="py-2 pr-3">Sınav</th>
                    <th className="py-2 pr-3">Durum</th>
                    <th className="py-2 pr-3 text-right">Skor</th>
                    <th className="py-2 pl-3">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentAttempts.slice(0, 30).map((a) => {
                    const name = a.exam.anonymousMode
                      ? (a.candidate.anonymousId ?? 'Anonim')
                      : (a.candidate.fullName ?? a.candidate.anonymousId ?? 'Aday');
                    return (
                      <tr key={a.id}>
                        <td className="py-2 pr-3">
                          <p className="text-sm text-slate-900">{name}</p>
                          {!a.exam.anonymousMode && a.candidate.email && (
                            <p className="text-xs text-slate-500">
                              {a.candidate.email}
                            </p>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs text-slate-700">
                          {a.exam.accessCode}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ${
                              STATUS_COLORS[a.status] ?? 'bg-slate-100'
                            }`}
                          >
                            {a.status}
                          </span>
                          {a.certificateId && (
                            <span
                              className="ml-1 inline-block"
                              title="Sertifika var"
                            >
                              🏅
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono">
                          {a.totalScore !== null
                            ? `${a.totalScore}/100`
                            : '—'}
                        </td>
                        <td className="py-2 pl-3 text-xs text-slate-500">
                          {new Date(a.startedAt).toLocaleDateString('tr-TR')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {org.internalNotes && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              İç Notlar
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
              {org.internalNotes}
            </p>
          </Card>
        )}
      </main>
    </div>
  );
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

// ─── Exam + Pre-provisioned Candidates Block ────────────────────

interface InvitedCandidate {
  id: string;
  fullName: string | null;
  email: string | null;
  position: string | null;
  experienceYears: string;
  invitationToken: string | null;
  invitationCreatedAt: string | null;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'ABANDONED';
  attempt: {
    id: string;
    totalScore: number | null;
    scoreLevel: string | null;
    completedAt: string | null;
    certificateId: string | null;
    tabSwitchCount: number;
  } | null;
}

const STATUS_BADGE: Record<string, string> = {
  NOT_STARTED: 'bg-slate-100 text-slate-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-800',
  COMPLETED: 'bg-green-50 text-green-800',
  EXPIRED: 'bg-red-50 text-red-700',
  ABANDONED: 'bg-slate-100 text-slate-600',
};

const STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: 'Başlamadı',
  IN_PROGRESS: 'Devam Ediyor',
  COMPLETED: 'Tamamlandı',
  EXPIRED: 'Süresi Doldu',
  ABANDONED: 'Terk',
};

function ExamWithCandidates(props: {
  examId: string;
  accessCode: string;
  questionCount: number;
  durationMin: number;
  anonymousMode: boolean;
  validUntil: string;
}): JSX.Element {
  const { examId, accessCode, questionCount, durationMin, anonymousMode, validUntil } = props;
  const [candidates, setCandidates] = useState<InvitedCandidate[] | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  async function refreshCandidates(): Promise<void> {
    try {
      const data = await api.get<{ candidates: InvitedCandidate[] }>(
        `/api/admin/exams/${examId}/candidates`,
      );
      setCandidates(data.candidates);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  useEffect(() => {
    void refreshCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  async function revoke(cid: string, name: string): Promise<void> {
    if (!confirm(`${name} davetini iptal et?`)) return;
    try {
      await api.delete(`/api/admin/candidates/${cid}`);
      await refreshCandidates();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function copyInvite(token: string): Promise<void> {
    const url = `${window.location.origin}/sinav/davet/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      // ignore
    }
  }

  const completedCount =
    candidates?.filter((c) => c.status === 'COMPLETED').length ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <code className="font-mono text-lg font-bold text-blue-900">
            {accessCode}
          </code>
          <p className="text-xs text-slate-500">
            {questionCount} soru · {durationMin} dk
            {anonymousMode && ' · anonim'}
            {' · '}
            Geçerli: {new Date(validUntil).toLocaleDateString('tr-TR')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-white px-3 py-1 text-xs text-slate-700 shadow-sm">
            {candidates?.length ?? 0} davet · {completedCount} tamamlandı
          </span>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            + Aday Ekle
          </Button>
        </div>
      </div>

      {showAdd && (
        <AddCandidateModal
          examId={examId}
          accessCode={accessCode}
          onClose={() => setShowAdd(false)}
          onCreated={async () => {
            setShowAdd(false);
            await refreshCandidates();
          }}
        />
      )}

      {error && (
        <div className="mt-3">
          <Alert variant="danger">{error}</Alert>
        </div>
      )}

      {candidates === null ? (
        <p className="mt-3 text-xs text-slate-500">Yükleniyor…</p>
      ) : candidates.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">
          Bu sınava davetli aday yok.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Aday</th>
                <th className="px-3 py-2">Davet Linki</th>
                <th className="px-3 py-2">Durum</th>
                <th className="px-3 py-2 text-right">Skor</th>
                <th className="px-3 py-2 text-right">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {candidates.map((c) => {
                const inviteUrl = c.invitationToken
                  ? `${window.location.origin}/sinav/davet/${c.invitationToken}`
                  : null;
                return (
                  <tr key={c.id}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900">
                        {c.fullName ?? '—'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.email ?? '—'}
                        {c.position && ` · ${c.position}`}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      {inviteUrl ? (
                        <button
                          type="button"
                          onClick={() => copyInvite(c.invitationToken!)}
                          className="rounded border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs text-blue-900 hover:bg-blue-100"
                          title={inviteUrl}
                        >
                          {copiedToken === c.invitationToken
                            ? '✓ Kopyalandı'
                            : '🔗 Kopyala'}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">iptal</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          STATUS_BADGE[c.status] ?? 'bg-slate-100'
                        }`}
                      >
                        {STATUS_LABEL[c.status] ?? c.status}
                      </span>
                      {c.attempt?.tabSwitchCount &&
                      c.attempt.tabSwitchCount > 0 ? (
                        <span
                          className="ml-1 text-xs text-amber-700"
                          title={`${c.attempt.tabSwitchCount} sekme değiş.`}
                        >
                          ⚠️
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.attempt?.totalScore !== null &&
                      c.attempt?.totalScore !== undefined ? (
                        <div>
                          <p className="font-mono font-bold text-blue-900">
                            {c.attempt.totalScore}/100
                          </p>
                          <p className="text-xs text-slate-500">
                            {c.attempt.scoreLevel}
                            {c.attempt.certificateId && ' 🏅'}
                          </p>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {c.status === 'NOT_STARTED' && c.invitationToken && (
                          <button
                            type="button"
                            onClick={() =>
                              revoke(c.id, c.fullName ?? c.email ?? c.id)
                            }
                            className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            İptal
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Add Exam Modal ─────────────────────────────────────────────

interface TemplateRow {
  id: string;
  name: string;
  durationMin: number;
  questionCount: number;
}

function AddExamModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [templates, setTemplates] = useState<TemplateRow[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ templates: TemplateRow[] }>('/api/manager/templates')
      .then((d) => {
        setTemplates(d.templates);
        if (d.templates[0]) setTemplateId(d.templates[0].id);
      })
      .catch(() => setError('Şablonlar yüklenemedi'));
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!templateId) {
      setError('Bir şablon seçin');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/admin/orgs/${orgId}/exams`, {
        templateId,
        anonymousMode,
      });
      await onCreated();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Sınav yaratılamadı');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">Yeni Sınav</h3>
        <p className="mt-1 text-sm text-slate-600">
          Şablon seç, BMU otomatik soruları stratifiye eder. Sonra adayları
          tek tek davet et.
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="Sektör Şablonu" htmlFor="x-tpl" required>
            <select
              id="x-tpl"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!templates}
            >
              {!templates && <option>Yükleniyor…</option>}
              {templates?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.questionCount} soru · {t.durationMin} dk)
                </option>
              ))}
            </select>
          </Field>

          <Checkbox
            id="x-anon"
            label="Anonim mod — adaylar ad/e-posta yerine takma adla görünür"
            checked={anonymousMode}
            onChange={(e) => setAnonymousMode(e.target.checked)}
          />

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!templateId}
            >
              Oluştur
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ─── Add Candidate Modal ─────────────────────────────────────────

interface CreatedCandidate {
  candidate: { id: string; fullName: string | null };
  invitationToken: string;
  invitationUrl: string;
}

const EXPERIENCE_BANDS = [
  { value: 'LT_1Y', label: '<1 yıl' },
  { value: 'Y1_TO_2', label: '1-2 yıl' },
  { value: 'Y2_TO_5', label: '2-5 yıl' },
  { value: 'GT_5Y', label: '5+ yıl' },
];

function AddCandidateModal({
  examId,
  accessCode,
  onClose,
  onCreated,
}: {
  examId: string;
  accessCode: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [experienceYears, setExperienceYears] = useState('Y2_TO_5');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedCandidate | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await api.post<CreatedCandidate>(
        `/api/admin/exams/${examId}/candidates`,
        {
          fullName,
          email: email || undefined,
          position: position || undefined,
          experienceYears,
          publicCertConsent: false,
        },
      );
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Aday eklenemedi');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const fullUrl = `${window.location.origin}${result.invitationUrl}`;
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
        <Card className="w-full max-w-md">
          <Alert variant="success">
            <strong>{result.candidate.fullName}</strong> davet edildi.
          </Alert>
          <div className="mt-3 text-sm">
            <p className="text-slate-600">Kişisel davet linki:</p>
            <div className="mt-2 rounded-lg border-2 border-blue-200 bg-blue-50 p-3">
              <p className="break-all font-mono text-xs text-slate-900">
                {fullUrl}
              </p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(fullUrl);
                }}
                className="mt-2 rounded-md border border-blue-300 px-3 py-1 text-xs font-medium text-blue-900 hover:bg-blue-100"
              >
                Linki Kopyala
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Bu linki adaya ilet — tek tıkla sınava başlar. Kayıt formu
              yok. Sınav: <code className="font-mono">{accessCode}</code>
            </p>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={async () => {
                await onCreated();
              }}
            >
              Tamamla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">
          Yeni Aday — {accessCode}
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Aday için kişisel davet linki üretilir. Link tek tıkla sınava götürür
          (kayıt formu yok).
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="Ad Soyad" htmlFor="c-name" required>
            <Input
              id="c-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </Field>
          <Field label="E-posta" htmlFor="c-email" hint="Opsiyonel — rapor maili için">
            <Input
              id="c-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
            />
          </Field>
          <Field label="Pozisyon" htmlFor="c-pos" hint="Opsiyonel">
            <Input
              id="c-pos"
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </Field>
          <Field label="SolidWorks Deneyimi" htmlFor="c-exp" required>
            <select
              id="c-exp"
              value={experienceYears}
              onChange={(e) => setExperienceYears(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {EXPERIENCE_BANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button type="submit" loading={submitting} disabled={!fullName}>
              Davet Et
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ─── Add Member Modal — firmaya bağlı yeni kullanıcı ─────────────

interface AddMemberResult {
  user: { id: string; email: string; name: string };
  initialPassword: string;
  note: string;
}

function AddMemberModal({
  orgId,
  orgName,
  onClose,
  onCreated,
}: {
  orgId: string;
  orgName: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'VIEWER'>('VIEWER');
  const [customPassword, setCustomPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AddMemberResult | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        email,
        name,
        isBmuStaff: false,
        attachToOrg: { organizationId: orgId, role },
      };
      if (customPassword && password) body['password'] = password;
      const data = await api.post<AddMemberResult>('/api/admin/users', body);
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Üye eklenemedi');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
        <Card className="w-full max-w-md">
          <Alert variant="success">
            <strong>Üye eklendi.</strong> {result.note}
          </Alert>
          <div className="mt-3 text-sm">
            <p>
              <strong>{result.user.name}</strong> · {result.user.email}
            </p>
            <p className="text-xs text-slate-500">
              {orgName} firmasına {role} olarak eklendi.
            </p>
          </div>
          <div className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">
              Şifre (tek seferlik)
            </p>
            <p className="mt-1 font-mono text-base text-slate-900">
              {result.initialPassword}
            </p>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(result.initialPassword);
              }}
              className="mt-2 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Kopyala
            </button>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={async () => {
                await onCreated();
              }}
            >
              Tamamla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">
          {orgName} — Yeni Üye
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Kullanıcı bu firmaya bağlı olarak yaratılır. Sadece ADMIN veya
          VIEWER rolü atanabilir; OWNER devri için ayrı bir akış var.
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="E-posta" htmlFor="m-email" required>
            <Input
              id="m-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              required
            />
          </Field>
          <Field label="İsim" htmlFor="m-name" required>
            <Input
              id="m-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>
          <Field label="Rol" htmlFor="m-role" required hint="ADMIN sınav oluşturabilir + adayları davet edebilir. VIEWER sadece raporları görür.">
            <select
              id="m-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'ADMIN' | 'VIEWER')}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="VIEWER">Görüntüleyici (VIEWER)</option>
              <option value="ADMIN">Admin (ADMIN)</option>
            </select>
          </Field>
          <Checkbox
            id="m-cpw"
            label="Şifreyi manuel gireyim (boş bırak → auto-generate)"
            checked={customPassword}
            onChange={(e) => setCustomPassword(e.target.checked)}
          />
          {customPassword && (
            <Field
              label="Şifre"
              htmlFor="m-pw"
              hint="En az 8 karakter, 1 büyük + 1 küçük + 1 rakam"
            >
              <Input
                id="m-pw"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!email || !name}
            >
              Üye Ekle
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
